/**
 * TileProvider - Servicio de tiles con fallback IGN → OSM
 *
 * Implementa medida G06: Fallback a OSM si IGN no disponible
 *
 * @module services/tiles/TileProvider
 * @version 1.0.0
 * @since V07
 *
 * ## Proveedores soportados
 * - **IGN WMTS**: PNOA, IGN Base, MTN (primario)
 * - **OSM**: OpenStreetMap (fallback)
 *
 * ## Características
 * - Detección automática de disponibilidad
 * - Fallback transparente a OSM
 * - Configuración agnóstica de librería de mapas
 * - Caché de estado de proveedores
 *
 * @example
 * ```typescript
 * const provider = new TileProvider();
 * const config = await provider.getActiveTileConfig();
 * // Usar config.url, config.attribution, etc.
 * ```
 */

// ============================================================================
// TIPOS
// ============================================================================

/** Identificadores de proveedores de tiles */
export type TileProviderId = 'ign-pnoa' | 'ign-base' | 'ign-mtn' | 'osm';

/** Estado de disponibilidad de un proveedor */
export type ProviderStatus = 'available' | 'unavailable' | 'checking' | 'unknown';

/** Configuración de un proveedor de tiles */
export interface TileConfig {
  /** Identificador único del proveedor */
  id: TileProviderId;
  /** Nombre legible del proveedor */
  name: string;
  /** Descripción breve */
  description: string;
  /** URL template para tiles (formato {z}/{x}/{y} o WMTS) */
  url: string;
  /** URL para verificar disponibilidad (GetCapabilities o tile de prueba) */
  healthCheckUrl: string;
  /** Atribución requerida */
  attribution: string;
  /** Zoom mínimo soportado */
  minZoom: number;
  /** Zoom máximo soportado */
  maxZoom: number;
  /** Subdominios (para balanceo de carga) */
  subdomains?: string[];
  /** Formato de imagen */
  format: 'png' | 'jpeg' | 'webp';
  /** Tipo de servicio */
  serviceType: 'WMTS' | 'TMS' | 'XYZ';
  /** Capa WMTS (si aplica) */
  layer?: string;
  /** MatrixSet WMTS (si aplica) */
  matrixSet?: string;
  /** Si es proveedor oficial español */
  isOfficial: boolean;
  /** Prioridad (menor = mayor prioridad) */
  priority: number;
}

/** Resultado de verificación de disponibilidad */
export interface HealthCheckResult {
  providerId: TileProviderId;
  status: ProviderStatus;
  responseTimeMs: number;
  checkedAt: Date;
  errorMessage?: string;
}

/** Configuración activa con información de fallback */
export interface ActiveTileConfig extends TileConfig {
  /** Si está usando fallback */
  isFallback: boolean;
  /** Proveedor original preferido (si en fallback) */
  preferredProviderId?: TileProviderId;
  /** Último health check */
  lastHealthCheck?: HealthCheckResult;
}

/** Opciones para el TileProvider */
export interface TileProviderOptions {
  /** Proveedor preferido */
  preferredProvider?: TileProviderId;
  /** Timeout para health check en ms */
  healthCheckTimeoutMs?: number;
  /** Habilitar fallback automático */
  enableFallback?: boolean;
  /** Intervalo de reintento en ms */
  retryIntervalMs?: number;
}

// ============================================================================
// CONFIGURACIONES DE PROVEEDORES
// ============================================================================

/**
 * Configuración del proveedor IGN PNOA (Ortoimágenes)
 * Plan Nacional de Ortofotografía Aérea - Máxima actualidad
 */
export const IGN_PNOA_CONFIG: TileConfig = {
  id: 'ign-pnoa',
  name: 'IGN PNOA',
  description: 'Ortofotografía aérea del Plan Nacional de Ortofotografía Aérea',
  url: 'https://www.ign.es/wmts/pnoa-ma?service=WMTS&request=GetTile&version=1.0.0&layer=OI.OrthoimageCoverage&style=default&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}&format=image/jpeg',
  healthCheckUrl: 'https://www.ign.es/wmts/pnoa-ma?service=WMTS&request=GetCapabilities',
  attribution: '© <a href="https://www.ign.es" target="_blank">Instituto Geográfico Nacional</a>',
  minZoom: 0,
  maxZoom: 20,
  format: 'jpeg',
  serviceType: 'WMTS',
  layer: 'OI.OrthoimageCoverage',
  matrixSet: 'GoogleMapsCompatible',
  isOfficial: true,
  priority: 1,
};

/**
 * Configuración del proveedor IGN Base (Cartografía base)
 * Mapa base oficial de España
 */
export const IGN_BASE_CONFIG: TileConfig = {
  id: 'ign-base',
  name: 'IGN Base',
  description: 'Cartografía base oficial del IGN',
  url: 'https://www.ign.es/wmts/ign-base?service=WMTS&request=GetTile&version=1.0.0&layer=IGNBaseTodo&style=default&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}&format=image/jpeg',
  healthCheckUrl: 'https://www.ign.es/wmts/ign-base?service=WMTS&request=GetCapabilities',
  attribution: '© <a href="https://www.ign.es" target="_blank">Instituto Geográfico Nacional</a>',
  minZoom: 0,
  maxZoom: 20,
  format: 'jpeg',
  serviceType: 'WMTS',
  layer: 'IGNBaseTodo',
  matrixSet: 'GoogleMapsCompatible',
  isOfficial: true,
  priority: 2,
};

/**
 * Configuración del proveedor IGN MTN (Mapa Topográfico Nacional)
 * Mapa topográfico tradicional
 */
export const IGN_MTN_CONFIG: TileConfig = {
  id: 'ign-mtn',
  name: 'IGN MTN',
  description: 'Mapa Topográfico Nacional',
  url: 'https://www.ign.es/wmts/mapa-raster?service=WMTS&request=GetTile&version=1.0.0&layer=MTN&style=default&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}&format=image/jpeg',
  healthCheckUrl: 'https://www.ign.es/wmts/mapa-raster?service=WMTS&request=GetCapabilities',
  attribution: '© <a href="https://www.ign.es" target="_blank">Instituto Geográfico Nacional</a>',
  minZoom: 0,
  maxZoom: 20,
  format: 'jpeg',
  serviceType: 'WMTS',
  layer: 'MTN',
  matrixSet: 'GoogleMapsCompatible',
  isOfficial: true,
  priority: 3,
};

/**
 * Configuración del proveedor OSM (fallback)
 * OpenStreetMap - Proveedor de respaldo gratuito
 */
export const OSM_CONFIG: TileConfig = {
  id: 'osm',
  name: 'OpenStreetMap',
  description: 'Mapa colaborativo mundial (fallback)',
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  healthCheckUrl: 'https://tile.openstreetmap.org/0/0/0.png',
  attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
  minZoom: 0,
  maxZoom: 19,
  subdomains: ['a', 'b', 'c'],
  format: 'png',
  serviceType: 'XYZ',
  isOfficial: false,
  priority: 100, // Última prioridad (fallback)
};

/** Mapa de todas las configuraciones disponibles */
export const TILE_CONFIGS: Record<TileProviderId, TileConfig> = {
  'ign-pnoa': IGN_PNOA_CONFIG,
  'ign-base': IGN_BASE_CONFIG,
  'ign-mtn': IGN_MTN_CONFIG,
  'osm': OSM_CONFIG,
};

/** Lista de proveedores ordenados por prioridad */
export const PROVIDERS_BY_PRIORITY: TileProviderId[] = [
  'ign-pnoa',
  'ign-base',
  'ign-mtn',
  'osm',
];

// ============================================================================
// TILE PROVIDER CLASS
// ============================================================================

/**
 * Servicio de gestión de proveedores de tiles con fallback automático
 */
export class TileProvider {
  private options: Required<TileProviderOptions>;
  private healthCache: Map<TileProviderId, HealthCheckResult> = new Map();
  private activeProvider: TileProviderId | null = null;

  /** Opciones por defecto */
  static readonly DEFAULT_OPTIONS: Required<TileProviderOptions> = {
    preferredProvider: 'ign-base',
    healthCheckTimeoutMs: 5000,
    enableFallback: true,
    retryIntervalMs: 30000,
  };

  constructor(options: TileProviderOptions = {}) {
    this.options = { ...TileProvider.DEFAULT_OPTIONS, ...options };
  }

  // --------------------------------------------------------------------------
  // MÉTODOS PÚBLICOS
  // --------------------------------------------------------------------------

  /**
   * Obtiene la configuración del proveedor activo
   * Si el preferido no está disponible, usa fallback
   */
  async getActiveTileConfig(): Promise<ActiveTileConfig> {
    const preferredId = this.options.preferredProvider;
    const healthResult = await this.checkProviderHealth(preferredId);

    if (healthResult.status === 'available') {
      this.activeProvider = preferredId;
      return {
        ...TILE_CONFIGS[preferredId],
        isFallback: false,
        lastHealthCheck: healthResult,
      };
    }

    // Fallback
    if (this.options.enableFallback) {
      const fallbackConfig = await this.findAvailableFallback(preferredId);
      if (fallbackConfig) {
        return fallbackConfig;
      }
    }

    // Si todo falla, devolver OSM sin verificar
    this.activeProvider = 'osm';
    return {
      ...OSM_CONFIG,
      isFallback: true,
      preferredProviderId: preferredId,
      lastHealthCheck: healthResult,
    };
  }

  /**
   * Verifica la disponibilidad de un proveedor específico
   */
  async checkProviderHealth(providerId: TileProviderId): Promise<HealthCheckResult> {
    // Verificar caché (válido por retryIntervalMs)
    const cached = this.healthCache.get(providerId);
    if (cached && this.isCacheValid(cached)) {
      return cached;
    }

    const config = TILE_CONFIGS[providerId];
    const startTime = performance.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.options.healthCheckTimeoutMs
      );

      const _response = await fetch(config.healthCheckUrl, {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors', // WMTS puede no soportar CORS
      });

      clearTimeout(timeoutId);

      const result: HealthCheckResult = {
        providerId,
        // Con no-cors, no podemos verificar status, asumimos disponible si no hay error
        status: 'available',
        responseTimeMs: Math.round(performance.now() - startTime),
        checkedAt: new Date(),
      };

      this.healthCache.set(providerId, result);
      return result;
    } catch (error) {
      const result: HealthCheckResult = {
        providerId,
        status: 'unavailable',
        responseTimeMs: Math.round(performance.now() - startTime),
        checkedAt: new Date(),
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      };

      this.healthCache.set(providerId, result);
      return result;
    }
  }

  /**
   * Verifica todos los proveedores y devuelve sus estados
   */
  async checkAllProviders(): Promise<HealthCheckResult[]> {
    const results = await Promise.all(
      PROVIDERS_BY_PRIORITY.map((id) => this.checkProviderHealth(id))
    );
    return results;
  }

  /**
   * Obtiene la configuración de un proveedor específico
   */
  getProviderConfig(providerId: TileProviderId): TileConfig {
    return TILE_CONFIGS[providerId];
  }

  /**
   * Lista todos los proveedores disponibles
   */
  getAllProviders(): TileConfig[] {
    return PROVIDERS_BY_PRIORITY.map((id) => TILE_CONFIGS[id]);
  }

  /**
   * Obtiene el proveedor actualmente activo
   */
  getActiveProviderId(): TileProviderId | null {
    return this.activeProvider;
  }

  /**
   * Fuerza el uso de un proveedor específico
   */
  setPreferredProvider(providerId: TileProviderId): void {
    this.options.preferredProvider = providerId;
    this.activeProvider = null; // Reset para forzar re-evaluación
  }

  /**
   * Limpia la caché de health checks
   */
  clearHealthCache(): void {
    this.healthCache.clear();
  }

  /**
   * Genera URL de tile para coordenadas específicas
   */
  getTileUrl(providerId: TileProviderId, z: number, x: number, y: number): string {
    const config = TILE_CONFIGS[providerId];
    let url = config.url
      .replace('{z}', z.toString())
      .replace('{x}', x.toString())
      .replace('{y}', y.toString());

    // Reemplazar subdominio si existe
    if (config.subdomains && config.subdomains.length > 0) {
      const subdomainIndex = (x + y) % config.subdomains.length;
      url = url.replace('{s}', config.subdomains[subdomainIndex]);
    }

    return url;
  }

  // --------------------------------------------------------------------------
  // MÉTODOS PRIVADOS
  // --------------------------------------------------------------------------

  /**
   * Busca un proveedor disponible como fallback
   */
  private async findAvailableFallback(
    excludeId: TileProviderId
  ): Promise<ActiveTileConfig | null> {
    const candidates = PROVIDERS_BY_PRIORITY.filter((id) => id !== excludeId);

    for (const candidateId of candidates) {
      const healthResult = await this.checkProviderHealth(candidateId);

      if (healthResult.status === 'available') {
        this.activeProvider = candidateId;
        return {
          ...TILE_CONFIGS[candidateId],
          isFallback: true,
          preferredProviderId: excludeId,
          lastHealthCheck: healthResult,
        };
      }
    }

    return null;
  }

  /**
   * Verifica si un resultado cacheado sigue siendo válido
   */
  private isCacheValid(result: HealthCheckResult): boolean {
    const now = Date.now();
    const cacheAge = now - result.checkedAt.getTime();
    return cacheAge < this.options.retryIntervalMs;
  }
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Determina si un proveedor es oficial español
 */
export function isOfficialSpanishProvider(providerId: TileProviderId): boolean {
  return TILE_CONFIGS[providerId].isOfficial;
}

/**
 * Obtiene la atribución combinada para múltiples proveedores
 */
export function getCombinedAttribution(providerIds: TileProviderId[]): string {
  const uniqueAttributions = new Set(
    providerIds.map((id) => TILE_CONFIGS[id].attribution)
  );
  return Array.from(uniqueAttributions).join(' | ');
}

/**
 * Verifica si un zoom level es soportado por el proveedor
 */
export function isZoomSupported(providerId: TileProviderId, zoom: number): boolean {
  const config = TILE_CONFIGS[providerId];
  return zoom >= config.minZoom && zoom <= config.maxZoom;
}

// ============================================================================
// INSTANCIA SINGLETON
// ============================================================================

/** Instancia singleton del TileProvider */
let tileProviderInstance: TileProvider | null = null;

/**
 * Obtiene la instancia singleton del TileProvider
 */
export function getTileProvider(options?: TileProviderOptions): TileProvider {
  if (!tileProviderInstance) {
    tileProviderInstance = new TileProvider(options);
  }
  return tileProviderInstance;
}

/**
 * Resetea la instancia singleton (útil para tests)
 */
export function resetTileProvider(): void {
  tileProviderInstance = null;
}

export default TileProvider;
